import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Loader2, FileText, ArrowRight, Wrench, Globe, Smartphone, Send, X, ScrollText, ChevronDown, Camera, Stethoscope, KeyRound } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WARRANTY_TERMS = [
    {
        title: "1 · What is covered", items: [
            "Fracture, chipping or delamination of the restoration under normal occlusal load and function.",
            "Debonding of layered ceramic/composite material from the substructure.",
            "Deviation of fit or shade from the approved digital design file at the time of delivery.",
        ],
    },
    {
        title: "2 · Remedy & worldwide validity", items: [
            "Remedy is limited to repair, replacement or refund of the original laboratory fee, at Toothrocket's discretion.",
            "A verified replacement is manufactured within 12 working hours from the original digital design file.",
            "Valid worldwide — not locked to the original clinic. Any licensed dentist, in any country, may scan the ToothPassport and submit a claim. The replacement is manufactured at our nearest laboratory and delivered free of charge.",
            "Clinical changes requested at remake (new preparation, new shade) are quoted separately as a new case.",
        ],
    },
    {
        title: "3 · Claim procedure", items: [
            "Claims are submitted on this ToothPassport page or through the treating dentist, quoting the case number.",
            "A clinical assessment by a licensed dentist is required to verify the failure mode.",
            "Toothrocket may request the failed restoration (or its fragments) for quality analysis before a replacement is approved.",
            "Claims are reviewed within 1 business day and tracked live on the claim tracking page.",
        ],
    },
    {
        title: "4 · What is not covered", items: [
            "Damage caused by trauma, accidents or misuse (e.g. biting hard foreign objects).",
            "Failure attributable to untreated bruxism or parafunction where a protective night guard was prescribed but not worn.",
            "Failures caused by untreated periodontal disease, secondary caries, endodontic failure or fracture of the supporting tooth or implant.",
            "Damage resulting from improper cementation, adjustment or modification by any party other than a licensed dentist.",
            "Normal wear, staining or discolouration from smoking, diet or inadequate oral hygiene.",
            "Loss or theft of the restoration.",
        ],
    },
    {
        title: "5 · Conditions & limitations", items: [
            "Coverage runs from the date of manufacture for the period shown on this passport and is tied to the original patient and case number; it is not transferable.",
            "The patient must maintain reasonable oral hygiene and attend routine dental check-ups.",
            "Liability is limited to the original laboratory fee. Toothrocket is not liable for chair-time, travel, accommodation or any indirect or consequential costs.",
            "A replacement restoration carries the remaining balance of the original warranty period.",
        ],
    },
    {
        title: "6 · Your statutory rights", items: [
            "This warranty is provided in addition to, and does not limit or affect, any statutory rights the patient may have under applicable consumer-protection law.",
        ],
    },
];

const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return iso; }
};

const remaining = (expiresAt) => {
    const ms = new Date(expiresAt) - new Date();
    if (ms <= 0) return null;
    const months = Math.floor(ms / (30.44 * 24 * 3600 * 1000));
    return `${Math.floor(months / 12)} years ${months % 12} months`;
};

/**
 * ToothPassport — public live warranty validation page. Opened by scanning
 * the QR on the restoration's card/packaging: /warranty/{case_number}.
 */
export default function WarrantyPassport() {
    const { caseNumber } = useParams();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [claimOpen, setClaimOpen] = useState(false);
    const [claimResult, setClaimResult] = useState(null);
    const [termsOpen, setTermsOpen] = useState(location.hash === "#terms");

    useEffect(() => {
        axios.get(`${API}/warranty/verify/${encodeURIComponent(caseNumber)}`)
            .then(({ data }) => setData(data))
            .catch((e) => setError(e?.response?.data?.detail || "Verification failed"));
    }, [caseNumber]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0B1120] via-[#0e2436] to-[#0B1120] text-white flex items-center justify-center px-4 py-16" data-testid="warranty-passport-page">
            <div className="w-full max-w-xl">
                <div className="text-center mb-6">
                    <div className="font-mono-tr text-[11px] tracking-[0.32em] uppercase text-cyan-300">ToothPassport™</div>
                    <div className="text-[12px] text-slate-400 mt-1">Live warranty verification · Toothrocket Dental Labs</div>
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-cyan-300/70" data-testid="wp-sovereignty">
                        Your records · Your sovereignty
                    </div>
                </div>

                {!data && !error && (
                    <div className="rounded-3xl bg-white/[0.05] border border-white/10 p-14 text-center">
                        <Loader2 className="animate-spin mx-auto text-cyan-300" />
                        <div className="mt-3 text-[13px] text-slate-300">Validating warranty…</div>
                    </div>
                )}

                {error && (
                    <div className="rounded-3xl bg-white/[0.05] border border-rose-400/30 p-10 text-center" data-testid="wp-error">
                        <ShieldAlert size={40} className="mx-auto text-rose-400 mb-3" />
                        <div className="text-[17px] font-semibold">No warranty found</div>
                        <p className="mt-2 text-[13px] text-slate-400">We couldn&rsquo;t find a warranty for case <span className="font-mono-tr text-slate-200">{caseNumber}</span>. Check the code on your card or contact your clinic.</p>
                    </div>
                )}

                {data && (
                    <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-cyan-500/10" data-testid="wp-card">
                        <div className={`px-8 py-6 flex items-center gap-4 ${data.valid ? "bg-emerald-500/15 border-b border-emerald-400/25" : "bg-rose-500/15 border-b border-rose-400/25"}`}>
                            <span className={`relative flex h-3 w-3 shrink-0 ${data.valid ? "" : "hidden"}`}>
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400" />
                            </span>
                            <ShieldCheck size={26} className={data.valid ? "text-emerald-300" : "text-rose-300"} />
                            <div>
                                <div className="text-[18px] font-semibold tracking-tight" data-testid="wp-status">
                                    {data.valid ? "Warranty active & verified" : "Warranty expired"}
                                </div>
                                {data.valid && remaining(data.expires_at) && (
                                    <div className="text-[12.5px] text-emerald-200/90">{remaining(data.expires_at)} remaining</div>
                                )}
                            </div>
                        </div>
                        <div className="bg-white/[0.04] backdrop-blur px-8 py-7">
                            {data.demo && (
                                <div className="mb-5 rounded-xl bg-cyan-400/10 border border-cyan-400/25 px-4 py-3 text-[12.5px] text-cyan-100" data-testid="wp-demo-note">
                                    <strong>This is a live demo passport.</strong> Every Toothrocket restoration ships with its own — scan it anytime, anywhere, forever. No PVC cards to store or lose.
                                </div>
                            )}
                            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-[13.5px]">
                                <div><dt className="text-slate-400 text-[11px] uppercase tracking-[0.16em] mb-1">Case number</dt><dd className="font-mono-tr text-cyan-200" data-testid="wp-case-number">{data.case_number}</dd></div>
                                <div><dt className="text-slate-400 text-[11px] uppercase tracking-[0.16em] mb-1">Patient</dt><dd>{data.patient_initials || "—"}</dd></div>
                                <div className="col-span-2"><dt className="text-slate-400 text-[11px] uppercase tracking-[0.16em] mb-1">Restoration</dt><dd data-testid="wp-restoration">{data.restoration}{data.shade ? ` · shade ${data.shade}` : ""}</dd></div>
                                <div><dt className="text-slate-400 text-[11px] uppercase tracking-[0.16em] mb-1">Manufactured</dt><dd>{fmtDate(data.issued_at)}</dd></div>
                                <div><dt className="text-slate-400 text-[11px] uppercase tracking-[0.16em] mb-1">Covered until</dt><dd className="text-emerald-300 font-medium" data-testid="wp-expires">{fmtDate(data.expires_at)}</dd></div>
                            </dl>
                            <div className="mt-6 pt-5 border-t border-white/10">
                                <div className="text-[12px] text-slate-400 leading-relaxed">
                                    <strong className="text-slate-200">The {data.warranty_years}-year promise:</strong> we replace, repair or refund
                                    any restoration that fails clinically — fractures, debonding or chipping under normal occlusal load —
                                    for a decade. Replacement printed within 12 hours of a claim.
                                </div>
                                <div className="mt-3 rounded-xl bg-cyan-400/10 border border-cyan-400/25 px-4 py-3 flex items-start gap-3" data-testid="wp-global-coverage">
                                    <Globe size={16} className="text-cyan-300 mt-0.5 shrink-0" />
                                    <div className="text-[12px] text-cyan-100 leading-relaxed">
                                        <strong className="text-white">Valid anywhere in the world.</strong> This passport is not locked to
                                        the original clinic. Moved to another country? Any dentist can scan it and request a replacement —
                                        we manufacture at our nearest laboratory and deliver <strong className="text-white">free of charge</strong>,
                                        anywhere in the world.
                                    </div>
                                </div>

                                {claimResult ? (
                                    <ClaimSuccess result={claimResult} />
                                ) : claimOpen ? (
                                    <ClaimForm caseNumber={data.case_number} onDone={setClaimResult} onClose={() => setClaimOpen(false)} onShowTerms={() => setTermsOpen(true)} />
                                ) : (
                                    <div className="mt-4 flex flex-wrap gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setClaimOpen(true)}
                                            data-testid="wp-claim-btn"
                                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-400 text-[#0B1120] hover:bg-cyan-300 text-[12.5px] font-semibold">
                                            <Wrench size={13} /> Start a warranty claim
                                        </button>
                                        <a href={`${API}/warranty/pass/${encodeURIComponent(data.case_number)}.png`}
                                            download={`ToothPassport-${data.case_number}.png`}
                                            data-testid="wp-wallet-btn"
                                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/15 hover:border-cyan-300 text-[12.5px] font-medium">
                                            <Smartphone size={13} /> Add to my phone
                                        </a>
                                        {!data.demo && (
                                            <a href={`${API}/cases/${encodeURIComponent(data.case_number)}/warranty.pdf`} target="_blank" rel="noreferrer" data-testid="wp-pdf-link"
                                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/15 hover:border-cyan-300 text-[12.5px] font-medium">
                                                <FileText size={13} /> Certificate (PDF)
                                            </a>
                                        )}
                                        <Link to="/" data-testid="wp-home-link"
                                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/15 hover:border-cyan-300 text-[12.5px] font-medium">
                                            toothrocket.com <ArrowRight size={13} />
                                        </Link>
                                    </div>
                                )}

                                {/* Terms & conditions — collapsible, deep-linkable via #terms */}
                                <div id="terms" className="mt-5 pt-4 border-t border-white/10">
                                    <button type="button" onClick={() => setTermsOpen((o) => !o)} data-testid="wp-terms-toggle"
                                        className="w-full flex items-center justify-between text-left group">
                                        <span className="inline-flex items-center gap-2 text-[12.5px] font-medium text-slate-300 group-hover:text-white transition">
                                            <ScrollText size={13} className="text-cyan-300" /> Warranty terms &amp; conditions
                                        </span>
                                        <ChevronDown size={15} className={`text-slate-400 transition-transform ${termsOpen ? "rotate-180" : ""}`} />
                                    </button>
                                    {termsOpen && (
                                        <div className="mt-4 space-y-4" data-testid="wp-terms">
                                            {WARRANTY_TERMS.map((s) => (
                                                <div key={s.title}>
                                                    <div className="text-[11px] font-mono-tr tracking-[0.14em] uppercase text-cyan-200 mb-1.5">{s.title}</div>
                                                    <ul className="space-y-1">
                                                        {s.items.map((it) => (
                                                            <li key={it} className="text-[11.5px] text-slate-400 leading-relaxed flex gap-2">
                                                                <span className="text-slate-600 shrink-0">·</span>{it}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="bg-[#0B1120] px-8 py-3.5 flex items-center justify-between text-[10.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-500">
                            <span>{data.lab}</span>
                            <span>Verified live · {new Date().toLocaleDateString("en-GB")}</span>
                        </div>
                    </div>
                )}

                {data && <ClinicRecordPanel caseNumber={data.case_number} />}
            </div>
        </div>
    );
}

const inputCls = "w-full rounded-xl bg-white/[0.06] border border-white/15 px-3.5 py-2.5 text-[13px] text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-300";

const ClaimForm = ({ caseNumber, onDone, onClose, onShowTerms }) => {
    const [form, setForm] = useState({ patient_name: "", email: "", phone: "", country: "", description: "" });
    const [photos, setPhotos] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const addPhotos = (e) => {
        const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
        setPhotos((p) => [...p, ...files].slice(0, 3));
        e.target.value = "";
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.patient_name || !form.email || !form.country || form.description.trim().length < 10) {
            toast.error("Please fill in your name, email, country and what happened (10+ characters).");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/warranty/claims`, { ...form, case_number: caseNumber });
            for (const photo of photos) {
                const fd = new FormData();
                fd.append("file", photo);
                try {
                    await axios.post(`${API}/warranty/claims/${data.claim_number}/photos`, fd);
                } catch {
                    toast.error(`Photo "${photo.name}" failed to upload — your claim is still submitted.`);
                }
            }
            onDone(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Claim submission failed — please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="mt-4 rounded-2xl bg-white/[0.05] border border-white/10 p-5" data-testid="wp-claim-form">
            <div className="flex items-center justify-between mb-4">
                <div className="text-[13.5px] font-semibold inline-flex items-center gap-2"><Wrench size={14} className="text-cyan-300" /> Warranty claim · case <span className="font-mono-tr text-cyan-200">{caseNumber}</span></div>
                <button type="button" onClick={onClose} data-testid="wp-claim-close" className="p-1.5 rounded-lg hover:bg-white/10"><X size={14} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <input value={form.patient_name} onChange={(e) => set("patient_name", e.target.value)} placeholder="Full name" data-testid="wp-claim-name" className={inputCls} />
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" data-testid="wp-claim-email" className={inputCls} />
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone (optional)" data-testid="wp-claim-phone" className={inputCls} />
                <input value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="Country you're in now" data-testid="wp-claim-country" className={inputCls} />
            </div>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="What happened? (fracture, debonding, chipping…)" data-testid="wp-claim-description" className={`${inputCls} mt-3 resize-none`} />

            {/* Photos of the damaged restoration */}
            <div className="mt-3">
                <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 hover:border-cyan-300 px-4 py-3 cursor-pointer text-[12.5px] text-slate-300 transition" data-testid="wp-claim-photo-label">
                    <Camera size={14} className="text-cyan-300" />
                    {photos.length ? `Add another photo (${photos.length}/3)` : "Attach photos of the damage (optional, up to 3)"}
                    <input type="file" accept="image/*" multiple onChange={addPhotos} className="hidden" data-testid="wp-claim-photo-input" disabled={photos.length >= 3} />
                </label>
                {photos.length > 0 && (
                    <div className="mt-3 flex gap-2" data-testid="wp-claim-photo-previews">
                        {photos.map((p, i) => (
                            <div key={`${p.name}-${i}`} className="relative">
                                <img src={URL.createObjectURL(p)} alt={p.name} className="w-16 h-16 rounded-lg object-cover border border-white/15" />
                                <button type="button" onClick={() => setPhotos((ph) => ph.filter((_, j) => j !== i))}
                                    data-testid={`wp-claim-photo-remove-${i}`}
                                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 text-white grid place-items-center">
                                    <X size={10} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <button type="submit" disabled={submitting} data-testid="wp-claim-submit"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-cyan-400 text-[#0B1120] hover:bg-cyan-300 disabled:opacity-60 text-[13px] font-semibold transition">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />} {submitting ? "Submitting…" : "Submit claim"}
            </button>
            <div className="mt-3 text-[11px] text-slate-500 text-center" data-testid="wp-claim-consent">
                By submitting this claim you confirm the information is accurate and agree to the{" "}
                <button type="button" onClick={onShowTerms} className="text-cyan-300 hover:underline" data-testid="wp-claim-terms-link">warranty terms &amp; conditions</button>.
                Coverage up to QAR 1,500 per patient per year · reviewed within 1 business day · replacement delivered free of charge, anywhere in the world.
            </div>
        </form>
    );
};

const ClaimSuccess = ({ result }) => (
    <div className="mt-4 rounded-2xl bg-emerald-500/10 border border-emerald-400/25 p-6 text-center" data-testid="wp-claim-success">
        <ShieldCheck size={30} className="mx-auto text-emerald-300 mb-2" />
        <div className="text-[15px] font-semibold">Claim submitted</div>
        <div className="mt-2 font-mono-tr text-[20px] text-cyan-200 tracking-wider" data-testid="wp-claim-number">{result.claim_number}</div>
        <p className="mt-2 text-[12.5px] text-slate-300 leading-relaxed">We&rsquo;ve emailed you this claim number with a live tracking link. Our clinical team reviews every claim within 1 business day.</p>
        <Link to={`/claim/${result.claim_number}`} data-testid="wp-claim-track-link"
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-400 text-[#0B1120] hover:bg-emerald-300 text-[12.5px] font-semibold">
            Track your claim live <ArrowRight size={13} />
        </Link>
    </div>
);

const RECORD_INCLUDES = [
    "Original digital design file",
    "Intraoral & lab scan files",
    "Shade, materials and specifications",
    "Full case + warranty history",
];

const ClinicRecordPanel = ({ caseNumber }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ clinic_name: "", dentist_name: "", email: "", phone: "", country: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.clinic_name || !form.dentist_name || !form.email || !form.country) {
            toast.error("Please fill in clinic, dentist, email and country.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/warranty/records/request`, { ...form, case_number: caseNumber });
            setResult(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Request failed — please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur overflow-hidden" data-testid="wp-record-panel">
            <div className="px-8 py-6">
                <div className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-400/30 grid place-items-center shrink-0"><Stethoscope size={17} className="text-cyan-300" /></span>
                    <div>
                        <div className="font-mono-tr text-[10px] tracking-[0.24em] uppercase text-cyan-300/80">For dental clinics</div>
                        <div className="mt-1 text-[16px] font-semibold tracking-tight">Treating this patient? Unlock the full clinical record history.</div>
                    </div>
                </div>

                {result ? (
                    <div className="mt-5 rounded-2xl bg-emerald-500/10 border border-emerald-400/25 p-5 text-center" data-testid="wp-record-success">
                        <KeyRound size={24} className="mx-auto text-emerald-300 mb-2" />
                        <div className="text-[14px] font-semibold">Request received</div>
                        <div className="mt-1.5 font-mono-tr text-[17px] text-cyan-200 tracking-wider" data-testid="wp-record-number">{result.request_number}</div>
                        <p className="mt-2 text-[12px] text-slate-300 leading-relaxed">
                            {result.is_network_member
                                ? "Your clinic is on the Toothmatrix network. We'll email your invoice within 1 business day — records are decrypted and delivered on payment."
                                : "Your clinic isn't on the Toothmatrix network yet. Joining is free — we've emailed you the registration link along with next steps."}
                        </p>
                        {!result.is_network_member && (
                            <Link to="/practice/register" data-testid="wp-record-join-link"
                                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-400 text-[#0B1120] hover:bg-cyan-300 text-[12px] font-semibold">
                                Join the network — free <ArrowRight size={12} />
                            </Link>
                        )}
                    </div>
                ) : open ? (
                    <form onSubmit={submit} className="mt-5" data-testid="wp-record-form">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <input value={form.clinic_name} onChange={(e) => set("clinic_name", e.target.value)} placeholder="Clinic name" data-testid="wp-record-clinic" className={inputCls} />
                            <input value={form.dentist_name} onChange={(e) => set("dentist_name", e.target.value)} placeholder="Dentist name" data-testid="wp-record-dentist" className={inputCls} />
                            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Clinic email" data-testid="wp-record-email" className={inputCls} />
                            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone (optional)" data-testid="wp-record-phone" className={inputCls} />
                        </div>
                        <input value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="Country" data-testid="wp-record-country" className={`${inputCls} mt-3`} />
                        <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={2} placeholder="Anything we should know? (optional)" data-testid="wp-record-message" className={`${inputCls} mt-3 resize-none`} />
                        <div className="mt-4 flex gap-3">
                            <button type="submit" disabled={submitting} data-testid="wp-record-submit"
                                className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-cyan-400 text-[#0B1120] hover:bg-cyan-300 disabled:opacity-60 text-[13px] font-semibold transition">
                                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />} Request record access
                            </button>
                            <button type="button" onClick={() => setOpen(false)} data-testid="wp-record-cancel"
                                className="px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-[12.5px]">Cancel</button>
                        </div>
                    </form>
                ) : (
                    <>
                        <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                            {RECORD_INCLUDES.map((r) => (
                                <li key={r} className="flex items-start gap-2 text-[12.5px] text-slate-300">
                                    <FileText size={12} className="text-cyan-300 mt-0.5 shrink-0" /> {r}
                                </li>
                            ))}
                        </ul>
                        <button type="button" onClick={() => setOpen(true)} data-testid="wp-record-btn"
                            className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-white/10 border border-white/15 hover:border-cyan-300 text-[13px] font-semibold transition">
                            <KeyRound size={14} className="text-cyan-300" /> Request record access · QAR 250
                        </button>
                    </>
                )}
            </div>
            <div className="bg-[#0B1120] px-8 py-3 text-[10.5px] text-slate-500 leading-relaxed" data-testid="wp-record-separation-note">
                The patient&rsquo;s warranty replacement is — and always stays — <span className="text-emerald-400">free of charge</span>.
                The QAR 250 service charge covers secure record maintenance, decryption and transfer only, and is reserved
                for Toothmatrix network practices (joining is free).
            </div>
        </div>
    );
};
